import { isString } from 'lodash';
import { ReactNode } from 'react';
import { Icon } from '@iconify/react';
import { useDropzone, DropzoneOptions } from 'react-dropzone';
import roundAddAPhoto from '@iconify/icons-ic/round-add-a-photo';
import { alpha, Theme, styled } from '@mui/material/styles';
import { Box, Typography, Paper, Button } from '@mui/material';
import { SxProps } from '@mui/system';
import { fData } from '../../utils/formatNumber';
import useLocales from 'hooks/useLocales';

const RootStyle = styled('div')(({ theme }) => ({
  width: 144,
  height: 144,
  margin: 'auto',
  padding: theme.spacing(1),
  
}));

const DropZoneStyle = styled('div')({
  zIndex: 0,
  width: '100%',
  height: '100%',
  outline: 'none',
  display: 'flex',
  overflow: 'hidden',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  '& > *': { width: '100%', height: '100%' },
  '&:hover': {
    cursor: 'pointer',
    '& .placeholder': {
      zIndex: 9
    }
  }
});

const PlaceholderStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.neutral,
  transition: theme.transitions.create('opacity', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter
  }),
  '&:hover': { opacity: 0.72 }
}));

// ----------------------------------------------------------------------

interface CustomFile extends File {
  path?: string;
  preview?: string;
}

interface UploadAvatarProps extends DropzoneOptions {
  error?: boolean;
  file: CustomFile | string | null;
  caption?: ReactNode;
  sx?: SxProps<Theme>;
  styles?: SxProps<Theme>;
  mainText?:string
}

export default function UploadAvatar({
  error,
  file,
  caption,
  sx,
  styles,
  mainText,
  ...other
}: UploadAvatarProps) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple: false,
    ...other
  });
  const { translate } = useLocales();

  const ShowRejectionItems = () => (
    <Paper
      variant="outlined"
      sx={{
        py: 1,
        px: 2,
        my: 2,
        borderColor: 'error.light',
        bgcolor: (theme) => alpha(theme.palette.error.main, 0.08)
      }}
    >
      {fileRejections.map(({ file, errors }) => {
        const { path, size }: CustomFile = file;
        return (
          <Box key={path} sx={{ my: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {path} - {fData(size)}
            </Typography>
            {errors.map((e) => (
              <Typography key={e.code} variant="caption" component="p" sx={{ display : 'flex' }}>
                - {e.code === 'file-too-large' ? `الملف المرفق أكبر من ${other.maxSize}` :e.code==='file-invalid-type' ? caption:  e.message}
              </Typography>
            ))}
          </Box>
        );
      })}
    </Paper>
  );

  return ( 
    <Box sx={{ display:'flex',gap:1,...styles,alignItems:'center' }}> 
      <RootStyle sx={sx}>
        <DropZoneStyle
          {...getRootProps()}
          sx={{
            ...(isDragActive && { opacity: 0.72 }),
            ...((isDragReject || error) && {
              color: 'error.main',
              borderColor: 'error.light',
              bgcolor: 'error.lighter'
            })
          }}
        >
          <input {...getInputProps()} />

          {file && (
            <Box
              component="img"
              alt="avatar"
              src={isString(file) ? file : file.preview}
              sx={{ zIndex: 8, objectFit: 'cover' }}
            />
          )}

          <PlaceholderStyle
            className="placeholder"
            sx={{
              ...(file && {
                opacity: 0,
                color: 'common.white',
                bgcolor: 'grey.900',
                '&:hover': { opacity: 0.72 }
              })
            }}
          >
            <Box component={Icon} icon={roundAddAPhoto} sx={{ width: 24, height: 24, mb: 1 }} />
            <Typography variant="caption">{file ? translate('upload an image') : translate('upload an image')}</Typography>
          </PlaceholderStyle>
        </DropZoneStyle>
      </RootStyle>
      <Box>
      {/* <Button sx={{ bgcolor:file?'primary.main':'', color:'white' }} color="error" >
          Delete
        </Button> */}

<Typography sx={{ color:file?'primary.main':'text.disabled' }} variant='h6'>{mainText}
</Typography>
      {caption}
      </Box>

      {fileRejections.length > 0 && <ShowRejectionItems />}
    </Box>
  );
}
